import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { Divider } from "@mui/material";
import { useChartPalette } from "../utils/chartUtils";
import { getChartTooltipStyle, getCartesianGridProps } from "../utils/chartStyles";
import { EmptyChartState } from "@/components/common/EmptyChartState";
import type { SpecificScatterPoint } from "@/types";
import { currencyCompactNotation, formatUSD } from "@/utils/formatters/currencyFormatter";
import { useAppTheme } from "@/hooks/useAppTheme";

interface SpecDeductibleCompositePremiumProps {
  data: SpecificScatterPoint[];
}

/**
 * Scatter plot of Spec Deductible vs Composite Premium per policy.
 * Each dot is colored by carrier.
 *
 * @param data - Pre-aggregated scatter points from the analytics endpoint.
 */
export const SpecDeductibleCompositePremium = ({ data: rawData }: SpecDeductibleCompositePremiumProps) => {
  const { theme } = useAppTheme();
  const chartPalette = useChartPalette();

  if (rawData.length === 0) {
    return <EmptyChartState />;
  }

  // Get unique carriers for consistent color assignment
  const carriers = [...new Set(rawData.map((d) => d.carrier_name))];

  const getColor = (carrier: string) => {
    const index = carriers.indexOf(carrier);
    return chartPalette[index % chartPalette.length];
  };

  const data = rawData.map((d) => ({
    x: d.specific_deductible,
    y: d.specific_composite_premium,
    carrier: d.carrier_name,
    policyholder: d.client_name,
  }));

  return (
    <ResponsiveContainer width="100%" height={280}>
      <ScatterChart margin={{ top: 5, right: 15, left: 5, bottom: 5 }}>
        <CartesianGrid {...getCartesianGridProps(theme)} />
        <XAxis
          type="number"
          dataKey="x"
          name="Specific Deductible"
          tickFormatter={(value) => formatUSD(value, currencyCompactNotation)}
          tickLine={false}
          axisLine={false}
          tick={{ fontSize: 10 }}
        />
        <YAxis
          type="number"
          dataKey="y"
          name="Composite Premium"
          tickFormatter={(value) => formatUSD(value)}
          tickLine={false}
          axisLine={false}
          tick={{ fontSize: 10 }}
        />
        <Tooltip
          content={({ payload }) => {
            if (!payload || !payload.length) return null;
            const { payload: item } = payload[0];
            return (
              <div
                style={{
                  ...getChartTooltipStyle(theme),
                  padding: "8px",
                }}
              >
                <strong>{item.policyholder}</strong>
                <div>{item.carrier}</div>
                <Divider sx={{ mb: 1, mt: 1 }} />
                <div>Composite Premium: {formatUSD(item.y)}</div>
                <div>Spec Deductible: {formatUSD(item.x, currencyCompactNotation)}</div>
              </div>
            );
          }}
        />
        <Scatter data={data}>
          {data.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={getColor(entry.carrier)} />
          ))}
        </Scatter>
      </ScatterChart>
    </ResponsiveContainer>
  );
};
